import React from "react";
import { ArtistDTO, Song } from "../../view/spotipitan/types";
import SquareImage from "../square-image/square-image";
import { handleCopy, shuffleList } from "../../../utils/utils";
import useSongStore from "../../view/spotipitan/store/SongState";
import "./artist-profile-header.scss";

interface Props {
    artist?: ArtistDTO;
    loading?: boolean;
    handleClickPlay?: (songs: Song[]) => void;
    summaryHeader?: boolean;
    summarySize?: number;
    onClickImage?: () => void;
}

const ArtistProfileHeader = ({
    artist,
    loading,
    handleClickPlay,
    summaryHeader,
    summarySize = 50,
    onClickImage
}: Props) => {
    const { setOriginalQueueOrder, setLoopMode, setRandomQueue } = useSongStore(state => state);

    if (!artist) return null;

    const artistName = artist.name || artist.username || "Artista";

    const handleClickPlayNormal = () => {
        if (!handleClickPlay || !artist.songs?.length) return;
        setLoopMode('none');
        setRandomQueue(false);
        setOriginalQueueOrder(artist.songs.map(x => x.id));
        handleClickPlay(artist.songs);
    };

    const handleClickShuffle = () => {
        if (!handleClickPlay || !artist.songs?.length) return;
        setLoopMode('none');
        setRandomQueue(true);
        setOriginalQueueOrder(artist.songs.map(x => x.id));
        handleClickPlay(shuffleList(artist.songs));
    };

    if (summaryHeader) {
        return (
            <div className="artist-profile-header__summary">
                <div className="artist-profile-header__summary_left">
                    <div className="artist-profile-header__summary__image" onClick={onClickImage} style={{ cursor: onClickImage ? "pointer" : "default" }}>
                        <SquareImage
                            playlist={artist}
                            src={artist.poster_url}
                            className="rounded-circle"
                            style={{ width: summarySize, height: summarySize }}
                        />
                    </div>
                    <div className="artist-profile-header__summary__info">
                        <div className="artist-profile-header__summary__info__name">
                            {artistName}
                        </div>
                        <div className="artist-profile-header__summary__info__type text-muted">
                            <small>
                                {artist.spotip17an_artist ? "Artista de Spotip17an" : "Artista"} • {artist.songs?.length || 0} canciones
                            </small>
                        </div>
                    </div>
                </div>
                <div className="artist-profile-header__actions">
                    {!!artist.songs?.length && handleClickPlay && (
                        <>
                            <i className="fa-solid fa-shuffle" onClick={handleClickShuffle} title="Aleatorio"></i>
                            <i
                                className="fa-solid fa-circle-play artist-profile-header__play-btn"
                                onClick={handleClickPlayNormal}
                                title="Reproducir"
                            ></i>
                        </>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="artist-profile-header">
            <div className="artist-profile-header__content">
                <div
                    className="artist-profile-header__avatar-container"
                    onClick={onClickImage}
                    style={{ cursor: onClickImage ? "pointer" : "default" }}
                >
                    <SquareImage
                        playlist={artist}
                        src={artist.poster_url}
                        big
                        className="rounded-circle"
                        style={{ width: 190, height: 190 }}
                    />
                </div>
                <div className="artist-profile-header__info">
                    <div className="artist-profile-header__badges">
                        {artist.spotip17an_artist ? (
                            <span className="badge text-bg-success">
                                <i className="fa-solid fa-circle-check me-1"></i> Artista de Spotip17an
                            </span>
                        ) : (
                            <span className="badge text-bg-secondary">
                                <i className="fa-solid fa-microphone me-1"></i> Artista
                            </span>
                        )}
                    </div>
                    <h1 className="artist-profile-header__name">{artistName}</h1>
                    <div className="artist-profile-header__stats">
                        {artist.subscribers && (
                            <span>
                                <i className="fa-solid fa-users me-1"></i>
                                {artist.subscribers}
                            </span>
                        )}
                        {artist.subscribers && !!artist.songs?.length && <span>•</span>}
                        {!!artist.songs?.length && (
                            <span>{artist.songs.length} canciones populares</span>
                        )}
                        {!!artist.playlists?.length && (
                            <>
                                <span>•</span>
                                <span>{artist.playlists.length} lanzamientos</span>
                            </>
                        )}
                    </div>
                    <div className="artist-profile-header__actions">
                        {!!artist.songs?.length && handleClickPlay && (
                            <>
                                <i
                                    className="fa-solid fa-circle-play artist-profile-header__play-btn"
                                    onClick={handleClickPlayNormal}
                                    title="Reproducir"
                                ></i>
                                <i
                                    className="fa-solid fa-shuffle"
                                    onClick={handleClickShuffle}
                                    title="Modo aleatorio"
                                ></i>
                            </>
                        )}
                        <i
                            className="fa-solid fa-share-nodes"
                            onClick={() => handleCopy(window.location.href)}
                            title="Compartir"
                        ></i>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ArtistProfileHeader;
